import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { featuresActions, sharedActions } from '../../../store';
import LoadingScreen from '../../shared/LoadingScreen/LoadingScreen';
import styles from './Visualizer.module.css';
import zoomreset from './assets/zoomreset.svg';
import undo from './assets/undo.svg';
import redo from './assets/redo.svg';


export default function Visualizer() {
    const dispatch = useDispatch();
    const vizZoom = useSelector(state => state.shared.vizZoom);
    const vizLoading = useSelector(state => state.shared.vizLoading);
    const activeTab = useSelector(state => state.shared.menuActiveTab);
    const vizSnapshots = useSelector(state => state.shared.vizSnapshots);
    const vizSnapIndex = useSelector(state => state.shared.vizSnapIndex);
    const isRestoringProject = useSelector(state => state.shared.isRestoringProject);
    const selectedInteriorFloorPlan = useSelector(state => state.features.selectedInteriorFloorPlan);
    
    // Pan/drag state for zoomed image
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const panStart = useRef({ x: 0, y: 0 });

    // Reset pan position when zoom resets to 1
    useEffect(() => {
        if (vizZoom === 1) {
            setPanPosition({ x: 0, y: 0 });
        }
    }, [vizZoom]);

    const handlePointerDown = useCallback((e) => {
        if (vizZoom <= 1) return;
        e.preventDefault();
        setIsDragging(true);
        dragStart.current = { x: e.clientX, y: e.clientY };
        panStart.current = { x: panPosition.x, y: panPosition.y };
        e.currentTarget.setPointerCapture(e.pointerId);
    }, [vizZoom, panPosition]);

    const handlePointerMove = useCallback((e) => {
        if (!isDragging) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPanPosition({
            x: panStart.current.x + dx,
            y: panStart.current.y + dy
        });
    }, [isDragging]);

    const handlePointerUp = useCallback((e) => {
        if (!isDragging) return;
        setIsDragging(false);
        e.currentTarget.releasePointerCapture(e.pointerId);
    }, [isDragging]);

    // Get specifier and selString from URL params
    const { specifier, selString } = useParams();
    const prevSpecifier = useRef(null);
    const prevSelString = useRef(null);
    const hasRestoredFloorPlan = useRef(false);
    const isLoadingRef = useRef(false);
    const pendingLoadRef = useRef(null);
    
    // Use ref to track activeTab so event listeners always get the current value
    const activeTabRef = useRef(activeTab);
    useEffect(() => {
        activeTabRef.current = activeTab;
    }, [activeTab]);

    // Restore floor plan data on refresh (when selectedInteriorFloorPlan is null but we have a specifier)
    // Skip this if we're restoring from a saved project (ChamProjects handles that)
    useEffect(() => {
        // Only run once on mount if we have a specifier but no interior floor plan set
        // Also skip if we're restoring from a saved project
        if (hasRestoredFloorPlan.current || !specifier || selectedInteriorFloorPlan || isRestoringProject) return;
        hasRestoredFloorPlan.current = true;
        
        const decodedSpecifier = decodeURIComponent(specifier);
        
        // Fetch floor plans to find matching interior
        const restoreFloorPlanData = async () => {
            try {
                const response = await fetch('https://lizard.chameleonpower.com/CDN/roomscenes/9759.json');
                if (!response.ok) return;
                
                const allFloorPlans = await response.json();
                
                // Find the current floor plan by specifier
                const currentFloorPlan = allFloorPlans.find(plan => plan.specifier === decodedSpecifier);
                
                if (currentFloorPlan) {
                    // Check if the current URL is for an interior/kitchen floor plan
                    const isInteriorUrl = currentFloorPlan.productCategory === 'Home Interior';
                    
                    // Extract the base name (e.g., "Byford" from "Byford Exterior" or "Byford Kitchen")
                    const baseName = currentFloorPlan.name
                        .replace(/\s*(Exterior|exterior|Kitchen|kitchen|Interior|interior)$/i, '')
                        .trim();
                    
                    if (isInteriorUrl) {
                        // URL is for kitchen/interior - set this as the interior floor plan and switch to kitchen tab
                        dispatch(featuresActions.setSelectedInteriorFloorPlan(currentFloorPlan));
                        dispatch(sharedActions.setMenuActiveTab('kitchen'));
                        dispatch(featuresActions.syncActiveSeriesForTab('kitchen'));
                        
                        // Find and set the matching exterior for the exterior tab
                        const matchingExterior = allFloorPlans.find(plan => 
                            plan.productCategory !== 'Home Interior' && 
                            plan.name.toLowerCase().includes(baseName.toLowerCase())
                        );
                        if (matchingExterior) {
                            dispatch(featuresActions.setVizDataForTab({
                                tab: 'exterior',
                                specifier: matchingExterior.specifier,
                                sel: '-1'
                            }));
                            
                            // Use exterior plan's description for floor plan info
                            const specs = matchingExterior.description ? matchingExterior.description.split(',') : [];
                            dispatch(featuresActions.setFloorPlanInfo({
                                name: matchingExterior.name.split(' ')[0],
                                sqft: specs[0] || '--',
                                beds: specs[1] || '--',
                                bath: specs[2] || '--',
                                garageCount: specs[3] || '--'
                            }));
                        }
                    } else {
                        // URL is for exterior - use current floor plan's description for floor plan info
                        const specs = currentFloorPlan.description ? currentFloorPlan.description.split(',') : [];
                        dispatch(featuresActions.setFloorPlanInfo({
                            name: currentFloorPlan.name.split(' ')[0],
                            sqft: specs[0] || '--',
                            beds: specs[1] || '--',
                            bath: specs[2] || '--',
                            garageCount: specs[3] || '--'
                        }));
                        
                        // Find matching interior
                        const matchingInterior = allFloorPlans.find(plan => 
                            plan.productCategory === 'Home Interior' && 
                            plan.name.toLowerCase().includes(baseName.toLowerCase())
                        );
                        
                        if (matchingInterior) {
                            dispatch(featuresActions.setSelectedInteriorFloorPlan(matchingInterior));
                            // Set the kitchen vizData with the interior specifier
                            dispatch(featuresActions.setVizDataForTab({
                                tab: 'kitchen',
                                specifier: matchingInterior.specifier,
                                sel: '-1'
                            }));
                        }
                    }
                }
            } catch (error) {
                console.error('Error restoring floor plan data:', error);
            }
        };
        
        restoreFloorPlanData();
    }, [specifier, selectedInteriorFloorPlan, isRestoringProject, dispatch]);

    // Load cham-viz when URL params change
    useEffect(() => {
        if (!specifier) return;
        
        const chamViz = document.getElementById('chamViz');
        if (!chamViz) return;

        // Check if specifier or selString changed
        const specifierChanged = prevSpecifier.current !== specifier;
        const selStringChanged = prevSelString.current !== selString;

        if (specifierChanged || selStringChanged) {
            // Decode the specifier from URL
            const decodedSpecifier = decodeURIComponent(specifier);
            
            // If already loading, queue this load for when ready fires
            if (isLoadingRef.current) {
                pendingLoadRef.current = { specifier: decodedSpecifier, sel: selString };
                prevSpecifier.current = specifier;
                prevSelString.current = selString;
                return;
            }
            
            console.log('[chamViz] loading:', decodedSpecifier, 'sel:', selString);
            // Load the visualizer with new params
            isLoadingRef.current = true;
            dispatch(sharedActions.setVizLoading(true));
            chamViz.setAttribute('spec', decodedSpecifier);
            chamViz.load(decodedSpecifier, selString);
            
            // Safety timeout: if ready never fires, unblock after 15s
            setTimeout(() => {
                if (isLoadingRef.current) {
                    console.warn('[chamViz] load timeout - unblocking');
                    isLoadingRef.current = false;
                    dispatch(sharedActions.setVizLoading(false));
                    if (pendingLoadRef.current) {
                        const { specifier: pSpec, sel: pSel } = pendingLoadRef.current;
                        pendingLoadRef.current = null;
                        isLoadingRef.current = true;
                        dispatch(sharedActions.setVizLoading(true));
                        const el = document.getElementById('chamViz');
                        el.setAttribute('spec', pSpec);
                        el.load(pSpec, pSel);
                    }
                }
            }, 15000);
            
            // Update refs
            prevSpecifier.current = specifier;
            prevSelString.current = selString;
            
        }
    }, [specifier, selString]);

        useEffect(() => {
        document.getElementById("chamViz").addEventListener('ready', function () {
            dispatch(sharedActions.setVizLoading(false));
            isLoadingRef.current = false;
            
            // If a load was queued while we were busy, execute it now
            if (pendingLoadRef.current) {
                const { specifier: pendingSpec, sel: pendingSel } = pendingLoadRef.current;
                pendingLoadRef.current = null;
                isLoadingRef.current = true;
                dispatch(sharedActions.setVizLoading(true));
                console.log('[chamViz] loading queued:', pendingSpec, 'sel:', pendingSel);
                const el = document.getElementById('chamViz');
                el.setAttribute('spec', pendingSpec);
                el.load(pendingSpec, pendingSel);
                return; // Skip surface detection until the queued load finishes
            }

            let hasStoneSurface = false
            document.getElementById('chamViz').applyByFilter("", "", function (newSel, currentSelectedSurface) {
                if(this.name == 'Stone')
                    hasStoneSurface = true;
            });

            if(hasStoneSurface){
                dispatch(featuresActions.setSceneSurfaces(['Stone/Brick']));
            } else {
                dispatch(featuresActions.setSceneSurfaces([]));
            }

        });

        document.getElementById("chamViz").addEventListener('surface_apply_done', function () {
            dispatch(sharedActions.setVizLoading(false));

            const chamViz = document.getElementById('chamViz');
            const vizData = chamViz.getSaveData();
            if (vizData) {
                const newUrl = `/visualizer/spec/${encodeURIComponent(vizData.specifier)}/sel/${vizData.sel}${window.location.search}${window.location.hash}`;
                window.history.replaceState(null, '', newUrl);
                
                // Update vizDataByTab in Redux for the current active tab (use ref for current value)
                dispatch(featuresActions.setVizDataForTab({
                    tab: activeTabRef.current,
                    specifier: vizData.specifier,
                    sel: vizData.sel
                }));

                if(activeTabRef.current == 'exterior'){
                    dispatch(featuresActions.setSelectedFloorPlanImageUrl(vizData.render));
                }
                
                // Snapshot the current state for undo/redo
                dispatch(sharedActions.snapshotVizState({
                    specifier: vizData.specifier,
                    sel: vizData.sel
                }));
            }
        });

        document.getElementById("chamViz").useMouseovers = false;
    }, [dispatch])

    
    const handleUndo = () => {
        dispatch(sharedActions.goBackward());
        // Load the previous snapshot
        const prevSnapshot = vizSnapshots[vizSnapIndex - 1];
        if (prevSnapshot) {
            dispatch(sharedActions.setVizLoading(true));
            const chamViz = document.getElementById('chamViz');
            chamViz.setAttribute('spec', prevSnapshot.specifier);
            chamViz.load(prevSnapshot.specifier, prevSnapshot.sel);
        }
    };

    const handleRedo = () => {
        dispatch(sharedActions.goForward());
        // Load the next snapshot
        const nextSnapshot = vizSnapshots[vizSnapIndex + 1];
        if (nextSnapshot) {
            dispatch(sharedActions.setVizLoading(true));
            const chamViz = document.getElementById('chamViz');
            chamViz.setAttribute('spec', nextSnapshot.specifier);
            chamViz.load(nextSnapshot.specifier, nextSnapshot.sel);
        }
    };

    const handleZoomIn = () => {
        dispatch(sharedActions.zoomIn());
    };

    const handleZoomOut = () => {
        dispatch(sharedActions.zoomOut());
    };

    const handleZoomReset = () => {
        dispatch(sharedActions.zoomReset());
        setPanPosition({ x: 0, y: 0 });
    };

    // Check if undo/redo are available
    const canUndo = vizSnapIndex > 0;
    const canRedo = vizSnapIndex < vizSnapshots.length - 1;

    return (
        <div className={styles.visualizer}>
            <LoadingScreen isOpen={vizLoading} />

            <div 
                className={styles.visualizerImage}
                style={{ 
                    transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${vizZoom})`, 
                    transformOrigin: 'center center',
                    cursor: vizZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    touchAction: vizZoom > 1 ? 'none' : 'auto'
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
            >
                  <cham-viz id="chamViz" spec="" width="1024" active="1"></cham-viz>
            </div>


            <div className={styles.controlButtons}>
                <button className={styles.controlButton} onClick={handleZoomIn} aria-label="Zoom in" title="Zoom in">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
                        <line x1="8" y1="5" x2="8" y2="11" stroke="currentColor" strokeWidth="2"/>
                        <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="2"/>
                        <line x1="13" y1="13" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                </button>
                <button 
                    className={`${styles.controlButton} ${vizZoom <= 1 ? styles.disabled : ''}`}
                    onClick={handleZoomOut} 
                    disabled={vizZoom <= 1}
                    aria-label="Zoom out" 
                    title="Zoom out"
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
                        <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="2"/>
                        <line x1="13" y1="13" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                </button>
                <button 
                    className={`${styles.controlButton} ${vizZoom === 1 ? styles.disabled : ''}`}
                    onClick={handleZoomReset}
                    disabled={vizZoom === 1}
                    aria-label="Reset zoom"
                    title="Reset zoom"
                >
                    <img src={zoomreset} width="24" height="24" alt="zoom reset"/>
                </button>
                <button 
                    className={`${styles.controlButton} ${!canUndo ? styles.disabled : ''}`}
                    onClick={handleUndo}
                    disabled={!canUndo}
                    aria-label="Undo"
                    title="Undo"
                >
                    <img src={undo} width="24" height="24" alt="undo"/>
                </button>
                <button 
                    className={`${styles.controlButton} ${!canRedo ? styles.disabled : ''}`}
                    onClick={handleRedo}
                    disabled={!canRedo}
                    aria-label="Redo"
                    title="Redo"
                >
                    <img src={redo} width="24" height="24" alt="redo"/>
                </button>
            </div>
        </div>
    );
}
